/**
 * URL Validation Service
 *
 * This service validates URLs against a whitelist of trusted domains
 * to ensure security when handling QR code scans.
 */

/**
 * Result of URL validation
 */
export interface ValidationResult {
    isValid: boolean;
    url: string;
    errorMessage: string | null;
}

class UrlValidationService {
    /**
     * List of trusted domains that are allowed in QR codes
     */
    private trustedDomains: string[] = [
        'museum.sketchpixel.com',
        'dev.museum.sketchpixel.com',
        'localhost'
    ];

    /**
     * Validates a URL against the list of trusted domains
     * @param url - The URL to validate
     * @returns ValidationResult object with validation status and details
     */
    validateUrl(url: string): ValidationResult {
        // Check if the URL is valid
        try {
            // Try to parse the URL to ensure it's valid
            const parsedUrl = new URL(url);

            // Check if the URL is from a trusted domain
            if (this.isFromTrustedDomain(parsedUrl.hostname)) {
                return {
                    isValid: true,
                    url,
                    errorMessage: null
                };
            } else {
                return {
                    isValid: false,
                    url,
                    errorMessage: `URL domain not trusted: ${parsedUrl.hostname}`
                };
            }
        } catch (error) {
            // If URL parsing fails, it's not a valid URL
            return {
                isValid: false,
                url,
                errorMessage: 'Invalid URL format'
            };
        }
    }

    /**
     * Checks if a hostname is in the list of trusted domains
     * @param hostname - The hostname to check
     * @returns True if the hostname is trusted, false otherwise
     */
    private isFromTrustedDomain(hostname: string): boolean {
        return this.trustedDomains.some(domain => {
            // Check for exact match or subdomain of trusted domain
            return hostname === domain || hostname.endsWith(`.${domain}`);
        });
    }
}

// Create a singleton instance
const urlValidationService = new UrlValidationService();

export default urlValidationService;
